import React from 'react';

/**
 * LocationCell - Standard industrial renderer for warehouse locations.
 * Unifies the display of Zone, Aisle, Rack, Level, and Container across all data grids.
 */
const LocationCell = ({ 
    location, 
    isDark = false, 
    large = false, 
    variant = 'amber', 
    hideCoordinates = false, 
    hideSection = false,
    horizontal = false,
    gridMode = false
}) => {
    const { section, aisle, rack, level, container } = location || {};
    const isEmerald = variant === 'emerald';
    
    // Empty state handling
    if (!section && !aisle && !rack && !level && !container) {
        return (
            <span className={`${isDark ? 'text-white/40' : 'text-slate-300'} font-bold text-[10px] uppercase tracking-widest italic flex items-center gap-2`}>
                <div className={`w-1 h-1 rounded-full ${isDark ? 'bg-white/20' : 'bg-slate-200'}`} />
                NO ZONE
            </span>
        );
    }

    const isLayoutHorizontal = horizontal && !gridMode;

    return (
        <div className={`flex ${isLayoutHorizontal ? 'flex-row items-center gap-3' : `flex-col ${gridMode ? 'justify-between flex-1 items-end text-right' : 'gap-1'}`} py-1 whitespace-nowrap overflow-visible`}>
            {/* Zone / Section - Primary Identifier */}
            {!hideSection && section && (
                <span className={`${isDark ? 'text-white' : 'text-slate-800'} font-black ${gridMode ? 'text-lg mb-1' : (large ? 'text-base' : 'text-xs')} uppercase tracking-tighter italic leading-tight`}>
                    {section}
                </span>
            )}
            
            {/* Detailed Coordinates - High-Density Badge Sequence */}
            {!hideCoordinates && (aisle || rack || level || container) && (
                <div className={`flex overflow-visible ${gridMode ? 'flex-col gap-1.5 mt-auto items-end' : 'flex-row items-center gap-1.5 mt-0.5'}`}>
                    {isLayoutHorizontal && !hideSection && section && (
                        <div className={`w-[1px] h-3 ${isDark ? 'bg-white/20' : 'bg-slate-200'} mx-1 shrink-0`} />
                    )}
                    
                    {aisle && (
                        <div className="flex items-center" title="Shelf / Cabinet">
                            <span className={`${gridMode ? 'text-[12px] px-3 py-1.5 w-fit' : (large ? 'text-[14px] px-3 py-1' : 'text-[11px] px-2 py-0.5')} font-mono font-black ${isDark ? (isEmerald ? 'bg-emerald-400 text-black shadow-lg shadow-emerald-400/20' : 'bg-amber-400 text-black shadow-lg shadow-amber-400/20') : (isEmerald ? 'bg-emerald-500/10 text-emerald-600 border-2 border-emerald-500/20 shadow-sm' : 'bg-amber-500/10 text-amber-600 border-2 border-amber-500/20 shadow-sm')} rounded-xl leading-none transition-all`}>
                                S:{aisle}
                            </span>
                        </div>
                    )}
                    {rack && (
                        <div className="flex items-center" title="Level / Floor">
                            <span className={`${gridMode ? 'text-[12px] px-3 py-1.5 w-fit' : (large ? 'text-[14px] px-3 py-1' : 'text-[11px] px-2 py-0.5')} font-mono font-black ${isDark ? 'bg-indigo-400 text-black shadow-lg shadow-indigo-400/20' : 'bg-indigo-500/10 text-indigo-600 border-2 border-indigo-500/20 shadow-sm'} rounded-xl leading-none transition-all`}>
                                L:{rack}
                            </span>
                        </div>
                    )}
                    {container && (
                        <div className="flex items-center" title="Container / Bin">
                            <span className={`${gridMode ? 'text-[12px] px-3 py-1.5 w-fit' : (large ? 'text-[14px] px-3 py-1' : 'text-[11px] px-2 py-0.5')} font-mono font-black ${isDark ? 'bg-white/20 text-white border-2 border-white/20' : 'bg-primary/5 text-primary border-2 border-primary/20 shadow-sm'} rounded-xl leading-none transition-all`}>
                                C:{container}
                            </span>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default React.memo(LocationCell);
